/**
 * B-588 — jedna lista rozwijana dla całego panelu (zamiennik natywnego `<select>`).
 *
 * Wygląd uzgodniony z właścicielem 12.10 (makieta „lista rozwijana — projekt"): pole z kafelkiem
 * ikony i okrągłym szewronem, stan „wybrane" z ramką w kolorze marki, a po kliknięciu okno NA ŚRODKU
 * EKRANU z dużymi wierszami (kółko → znacznik), wyszukiwarką od 8 pozycji i obsługą klawiatury
 * (strzałki, Enter, Esc, pierwsza litera). 12.10 właściciel wolał okno na środku od okienka pod polem
 * i od arkusza z dołu — tak samo działa aplikacja instruktora (`pwa/src/components/ListaWyboru.tsx`).
 *
 * ── Zgodność z natywnym `<select>` ────────────────────────────────────────────────────
 * Komponent jest zamiennikiem „1:1 w wywołaniu": dzieci to zwykłe `<option>` / `<optgroup>`,
 * `onChange` dostaje obiekt z `target.value` (string, jak w DOM) i `currentTarget.value`,
 * a `value` / `disabled` / `name` / `id` / `title` / `className` znaczą to samo. Dzięki temu
 * przejście z `<select>` to zmiana nazwy znacznika, bez przepisywania obsługi. `className`
 * z wywołania służy WYŁĄCZNIE do rozmieszczenia (szerokość, marginesy, flex/grid) — kolory,
 * obramowanie i wysokość pola wyznacza komponent, żeby wszystkie listy wyglądały tak samo.
 * Rozmiar kompaktowy (paski filtrów, tabele) wybiera się `wariant="kompakt"` albo wynika
 * z `className` (`text-xs`, `h-7`/`h-8`, `py-1`) — tak jak dotąd wynikał z niego rozmiar `<select>`.
 *
 * Opcja o wartości pustej (`<option value="">…`) jest „brakiem wyboru": pokazuje się jako
 * tekst zastępczy w polu i pozostaje pierwszym wierszem listy (wybór = wyczyszczenie).
 */
import {
  Children, Fragment, isValidElement, useCallback, useEffect, useId,
  useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactElement, type ReactNode, type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, List, Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

/** Zdarzenie przekazywane do `onChange` — wystarczająco podobne do `ChangeEvent<HTMLSelectElement>`,
 *  żeby istniejące obsługi (`e.target.value`) działały bez zmian. */
export interface ListaZdarzenie {
  target: { value: string; name?: string };
  currentTarget: { value: string; name?: string };
}

/** Zapis metodą daje bivariancję parametru: stare obsługi typowane jako `ChangeEvent<HTMLSelectElement>`
 *  (podtyp `ListaZdarzenie`) nadal się kompilują, bez przepisywania wywołań. */
export type ListaOnChange = { bivarianceHack(e: ListaZdarzenie): void }['bivarianceHack'];

export interface ListaProps {
  value?: string | number | null;
  onChange?: ListaOnChange;
  /** Uchwyt przycisku pola (np. do ustawienia fokusu). */
  ref?: Ref<HTMLButtonElement>;
  onKeyDown?: (e: ReactKeyboardEvent) => void;
  disabled?: boolean;
  name?: string;
  id?: string;
  title?: string;
  className?: string;
  /** Ikona kafelka po lewej (domyślnie lista); w polu z wybraną wartością zastępuje ją znacznik. */
  ikona?: ReactNode;
  wariant?: 'pole' | 'kompakt';
  /** Pole z błędem walidacji (czerwona ramka). */
  blad?: boolean;
  /** Tytuł okienka (domyślnie bez nagłówka). */
  tytul?: string;
  /** Tekst pustego pola, gdy lista nie ma opcji o wartości pustej. */
  placeholder?: string;
  'aria-label'?: string;
  autoFocus?: boolean;
  required?: boolean;
  children?: ReactNode;
}

interface Opcja { wartosc: string; etykieta: string; wylaczona: boolean; grupa?: string; }

/** Etykieta opcji bywa złożona z wielu dzieci (`{a} — {b}`): bierzemy sam tekst. */
function tekstZ(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(tekstZ).join('');
  if (isValidElement(node)) return tekstZ((node.props as { children?: ReactNode }).children);
  return '';
}

/** Spłaszcza `<option>` / `<optgroup>` / fragmenty / tablice w płaską listę. */
function zbierzOpcje(children: ReactNode, grupa?: string, out: Opcja[] = []): Opcja[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const el = child as ReactElement<{ value?: string | number; disabled?: boolean; label?: string; children?: ReactNode }>;
    if (el.type === Fragment) { zbierzOpcje(el.props.children, grupa, out); return; }
    if (el.type === 'optgroup') { zbierzOpcje(el.props.children, el.props.label, out); return; }
    if (el.type === 'option') {
      const etykieta = tekstZ(el.props.children);
      out.push({
        wartosc: el.props.value != null ? String(el.props.value) : etykieta,
        etykieta,
        wylaczona: !!el.props.disabled,
        grupa,
      });
    }
  });
  return out;
}

/** Klasy rozmieszczenia przenoszone z wywołania na opakowanie. */
const UKLAD = /^(?:[a-z-]+:)*(?:w-|min-w-|max-w-|flex-|shrink|grow|basis-|m[tblrxy]?-|col-|row-|self-|justify-self|order-|hidden|block|inline|absolute|relative|sticky|top-|left-|right-|bottom-|z-)/;
const KOMPAKT = /(?:^|\s)(?:text-xs|text-\[1[01]px\]|text-\[12px\]|h-[5-8]|py-0\.5|py-1|py-1\.5)(?=\s|$)/;
const PROG_WYSZUKIWARKI = 8;

function opisLiczby(n: number): string {
  const dwie = n % 100; const jedn = n % 10;
  return `${n} ${n === 1 ? 'pozycja' : jedn >= 2 && jedn <= 4 && !(dwie >= 12 && dwie <= 14) ? 'pozycje' : 'pozycji'}`;
}

export function Lista({
  value, onChange, disabled, name, id, title, className, ikona, wariant, blad, tytul, placeholder, children,
  autoFocus, required, ref, onKeyDown, ...aria
}: ListaProps) {
  const opcje = useMemo(() => zbierzOpcje(children), [children]);
  const wybor = value == null ? '' : String(value);
  const wybrana = opcje.find((o) => o.wartosc === wybor);
  const tekstZastepczy = opcje.find((o) => o.wartosc === '')?.etykieta ?? placeholder ?? 'Wybierz…';
  const maWybor = !!wybrana && wybrana.wartosc !== '';
  const kompakt = (wariant ?? (className && KOMPAKT.test(className) ? 'kompakt' : 'pole')) === 'kompakt';

  const [otwarta, setOtwarta] = useState(false);
  const [szukaj, setSzukaj] = useState('');
  const [aktywna, setAktywna] = useState(0);
  const przycisk = useRef<HTMLButtonElement>(null);
  const okno = useRef<HTMLDivElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const idListy = useId();

  const wyszukiwarka = opcje.filter((o) => o.wartosc !== '').length >= PROG_WYSZUKIWARKI;
  const widoczne = useMemo(() => {
    const q = szukaj.trim().toLowerCase();
    return q ? opcje.filter((o) => o.etykieta.toLowerCase().includes(q)) : opcje;
  }, [opcje, szukaj]);

  const zamknij = useCallback((zwrocFokus = true) => {
    setOtwarta(false); setSzukaj('');
    if (zwrocFokus) przycisk.current?.focus();
  }, []);

  const wybierz = useCallback((o: Opcja) => {
    if (o.wylaczona) return;
    onChange?.({ target: { value: o.wartosc, name }, currentTarget: { value: o.wartosc, name } });
    zamknij();
  }, [onChange, name, zamknij]);

  useEffect(() => {
    if (!otwarta) return;
    setAktywna(Math.max(0, widoczne.findIndex((o) => o.wartosc === wybor)));
    /* Okno jest na środku ekranu: fokus trafia do niego (bez wyszukiwarki), żeby działała klawiatura. */
    if (!wyszukiwarka) okno.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otwarta]);

  useEffect(() => {
    if (!otwarta) return;
    listaRef.current?.querySelector<HTMLElement>(`[data-i="${aktywna}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [aktywna, otwarta]);

  const klawisz = (e: ReactKeyboardEvent) => {
    onKeyDown?.(e);
    if (disabled || e.defaultPrevented) return;
    if (!otwarta) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOtwarta(true); }
      return;
    }
    if (e.key === 'Escape') {
      /* Esc zamyka TYLKO listę: okna formularzy i modalne słuchają Esc na `window`, więc bez tego zamknęłyby się razem z nią. */
      e.preventDefault(); e.stopPropagation(); zamknij();
    }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setAktywna((i) => Math.min(widoczne.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktywna((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setAktywna(0); }
    else if (e.key === 'End') { e.preventDefault(); setAktywna(widoczne.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); if (widoczne[aktywna]) wybierz(widoczne[aktywna]); }
    else if (e.key === 'Tab') zamknij(false);
    else if (!wyszukiwarka && e.key.length === 1 && /\S/.test(e.key)) {
      const q = e.key.toLowerCase();
      const n = widoczne.length;
      for (let k = 1; k <= n; k++) {
        const i = (aktywna + k) % n;
        if (widoczne[i].etykieta.toLowerCase().startsWith(q)) { setAktywna(i); break; }
      }
    }
  };

  const tytulOkna = tytul ?? aria['aria-label'] ?? title ?? 'Wybierz z listy';
  const uklad = (className ?? '').split(/\s+/).filter((c) => c && UKLAD.test(c)).join(' ');

  return (
    <div className={cn('relative inline-block max-w-full align-middle', uklad)} onKeyDown={klawisz}>
      <button
        ref={(el) => {
          przycisk.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) (ref as { current: HTMLButtonElement | null }).current = el;
        }}
        type="button"
        id={id}
        title={title}
        name={name}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-haspopup="listbox"
        aria-expanded={otwarta}
        aria-controls={otwarta ? idListy : undefined}
        aria-required={required || undefined}
        aria-invalid={blad || undefined}
        aria-label={aria['aria-label']}
        onClick={() => !disabled && setOtwarta((o) => !o)}
        className={cn(
          'group flex w-full items-center text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          kompakt ? 'min-h-8 gap-1.5 rounded-lg border px-2 py-1 text-xs' : 'min-h-11 gap-2.5 rounded-xl border px-2 py-1.5 text-sm',
          blad
            ? 'border-destructive bg-destructive/5'
            : maWybor
              ? 'border-primary/50 bg-primary/5'
              : 'border-border bg-card hover:border-foreground/30',
          disabled && 'cursor-not-allowed opacity-60',
          otwarta && !blad && 'border-primary ring-2 ring-primary/20',
        )}
      >
        {!kompakt && (
          <span className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            blad ? 'bg-card text-destructive' : maWybor ? 'bg-primary/15 text-primary' : 'bg-primary/10 text-primary/70',
          )}>
            {maWybor ? <Check className="h-4 w-4" /> : (ikona ?? <List className="h-4 w-4" />)}
          </span>
        )}
        <span className={cn('min-w-0 flex-1 truncate', maWybor ? 'font-medium text-foreground' : 'text-muted-foreground', kompakt && 'pl-0.5')}>
          {maWybor ? wybrana!.etykieta : tekstZastepczy}
        </span>
        <span className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-transform',
          kompakt ? 'h-5 w-5' : 'h-7 w-7',
          otwarta && 'rotate-180',
        )}>
          <ChevronDown className={kompakt ? 'h-3 w-3' : 'h-4 w-4'} />
        </span>
      </button>

      {/* Warstwa z-[12000] — ponad KAŻDYM oknem panelu: okno urządzenia i podgląd w Kreatorze mają z-[11000]
          i lista otwierała się pod nimi (wybór poza pierwszą pozycją był niemożliwy — testy Kontroli prędkości,
          29.10.2026). Strażnik `data-godzina` pilnuje, żeby żadna nakładka nie weszła wyżej. */}
      {otwarta && createPortal(
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/50 p-4">
          <button type="button" aria-label="Zamknij" tabIndex={-1} className="absolute inset-0 cursor-default" onClick={() => zamknij()} />
          <div
            ref={okno}
            role="dialog"
            aria-modal="true"
            aria-label={tytulOkna}
            tabIndex={-1}
            className="relative flex max-h-[80vh] w-full max-w-md flex-col rounded-3xl border bg-popover p-3.5 text-popover-foreground shadow-2xl outline-none"
          >
            <div className="flex items-center gap-2.5 pb-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                {ikona ?? <List className="h-[18px] w-[18px]" />}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold text-foreground">{tytulOkna}</h3>
                <p className="text-xs text-muted-foreground">{opisLiczby(opcje.filter((o) => o.wartosc !== '').length)}{required ? ' · wymagane' : ''}</p>
              </div>
              <button type="button" onClick={() => zamknij()} aria-label="Zamknij" className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            {wyszukiwarka && (
              <div className="mb-2 flex h-10 items-center gap-2 rounded-xl border bg-card px-3 text-sm">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  autoFocus
                  value={szukaj}
                  onChange={(e) => { setSzukaj(e.target.value); setAktywna(0); }}
                  placeholder="Szukaj na liście…"
                  className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            <div id={idListy} role="listbox" aria-label={tytulOkna} ref={listaRef} className="-mr-1 min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
              {widoczne.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">Brak wyników.</p>}
              {widoczne.map((o, i) => {
                const zaznaczona = o.wartosc === wybor && o.wartosc !== '';
                const poprzednia = widoczne[i - 1];
                return (
                  <Fragment key={`${o.grupa ?? ''}|${o.wartosc}|${i}`}>
                    {o.grupa && o.grupa !== poprzednia?.grupa && (
                      <p className="px-2 pb-0.5 pt-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{o.grupa}</p>
                    )}
                    <button
                      type="button"
                      role="option"
                      data-i={i}
                      aria-selected={zaznaczona}
                      disabled={o.wylaczona}
                      onMouseEnter={() => setAktywna(i)}
                      onClick={() => wybierz(o)}
                      className={cn(
                        'flex min-h-11 w-full items-center gap-3 rounded-[14px] border px-3 py-2 text-left text-sm transition-colors',
                        zaznaczona
                          ? 'border-primary/60 bg-primary/10 font-medium text-foreground'
                          : cn('border-border bg-card', o.wartosc === '' ? 'text-muted-foreground' : 'text-foreground', i === aktywna && 'border-primary/30 bg-primary/5'),
                        o.wylaczona && 'cursor-not-allowed opacity-40',
                      )}
                    >
                      <span className={cn(
                        'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
                        zaznaczona ? 'border-primary bg-primary text-primary-foreground' : 'border-foreground/25',
                      )}>
                        {zaznaczona && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{o.etykieta || '—'}</span>
                    </button>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
